import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { type KeyboardEvent, useEffect, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router';
import {
  COMPLETENESS,
  COMPLETENESS_LABEL,
  type Completeness,
  CONDITION_LABEL,
  CONDITIONS,
  type Condition,
  type Fitment,
  formatDay,
  type FileRef,
  type ProductAdmin,
  RACK_TYPE_LONG,
  RACK_TYPE_SHORT,
  RACK_TYPES,
  type RackType,
  REPLACED_PARTS,
  STEERING,
  STEERING_LABEL,
  type Steering,
  type StockResponse,
  type Unit,
  UNIT_SOURCE_LABEL,
  UNIT_STATE_LABEL,
} from '@reikraft/shared';
import { CardMedia, Choices, Dot, ErrorBox, Field, Loader, Modal, Panel, PhotoGrid, Segmented, Switch, useToast } from '@reikraft/ui';
import { api, errorText, uploadFiles } from '../api';
import { FormMessage, Hint, PageHead, useFormErrors } from '../components/common';
import { formatMoney, kopToRub, rubToKop } from '../format';
import { useFlag } from '../session';

interface Form {
  rackType: RackType;
  car: string;
  years: string;
  manufacturer: string;
  steering: Steering;
  completeness: Completeness;
  condition: Condition;
  warrantyMonths: number;
  replacedParts: string[];
  description: string;
  oems: string[];
  fitments: Fitment[];
  price: string;
  exchangePrice: string;
  partnerPrice: string;
  showOnSite: boolean;
  showToPartners: boolean;
  photos: FileRef[];
}

const EMPTY: Form = {
  rackType: 'hps',
  car: '',
  years: '',
  manufacturer: '',
  steering: 'left',
  completeness: 'none',
  condition: 'restored',
  warrantyMonths: 12,
  replacedParts: ['Сальники и уплотнения', 'Опорные втулки', 'Пыльники'],
  description: '',
  oems: [],
  fitments: [],
  price: '',
  exchangePrice: '',
  partnerPrice: '',
  showOnSite: true,
  showToPartners: false,
  photos: [],
};

function fromProduct(p: ProductAdmin): Form {
  return {
    rackType: p.rackType,
    car: p.car,
    years: p.years ?? '',
    manufacturer: p.manufacturer ?? '',
    steering: p.steering,
    completeness: p.completeness,
    condition: p.condition,
    warrantyMonths: p.warrantyMonths,
    replacedParts: p.replacedParts,
    description: p.description ?? '',
    oems: p.oems,
    fitments: p.fitments,
    price: kopToRub(p.priceKop),
    exchangePrice: kopToRub(p.exchangePriceKop),
    partnerPrice: kopToRub(p.partnerPriceKop),
    showOnSite: p.showOnSite,
    showToPartners: p.showToPartners,
    photos: p.photos,
  };
}

const toBody = (f: Form) => ({
  rackType: f.rackType,
  car: f.car,
  years: f.years || null,
  manufacturer: f.manufacturer || null,
  steering: f.steering,
  completeness: f.completeness,
  condition: f.condition,
  warrantyMonths: f.warrantyMonths,
  replacedParts: f.replacedParts,
  description: f.description || null,
  oems: f.oems,
  fitments: f.fitments.filter((x) => x.make && x.model),
  priceKop: rubToKop(f.price),
  exchangePriceKop: rubToKop(f.exchangePrice),
  partnerPriceKop: rubToKop(f.partnerPrice),
  showOnSite: f.showOnSite,
  showToPartners: f.showToPartners,
  photoIds: f.photos.map((p) => p.id),
});

export function ProductPage() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const toast = useToast();
  const errs = useFormErrors();
  const partnerPrices = useFlag('partner_prices');
  const b2b = useFlag('b2b_cabinet');

  const existing = useQuery({ queryKey: ['product', id], queryFn: () => api.get<ProductAdmin>(`/admin/products/${id}`), enabled: !!id });
  const stock = useQuery({ queryKey: ['stock', ''], queryFn: () => api.get<StockResponse>('/admin/stock'), enabled: !id });

  const [f, setF] = useState<Form>(EMPTY);
  const [source, setSource] = useState<'order' | 'buyout' | 'new'>(params.get('unit') ? 'order' : 'buyout');
  const [unitId, setUnitId] = useState<string | null>(params.get('unit'));
  const [count, setCount] = useState(1);
  const [place, setPlace] = useState('');
  const [oemDraft, setOemDraft] = useState('');
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (existing.data) setF(fromProduct(existing.data));
  }, [existing.data]);

  const awaiting = stock.data?.awaiting ?? [];
  useEffect(() => {
    if (id || source !== 'order') return;
    const a = awaiting.find((x) => x.unitId === unitId) ?? awaiting[0];
    if (a) {
      setUnitId(a.unitId);
      setF((x) => ({ ...x, car: x.car || (a.car ?? ''), rackType: a.rackType ?? x.rackType }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stock.data, source]);

  const set = <K extends keyof Form>(k: K, v: Form[K]) => setF((x) => ({ ...x, [k]: v }));

  const save = useMutation({
    mutationFn: async (publish: boolean) => {
      let p: ProductAdmin;
      if (id) {
        p = await api.put<ProductAdmin>(`/admin/products/${id}`, toBody(f));
      } else {
        p = await api.post<ProductAdmin>('/admin/products', toBody(f));
        if (source === 'order' && unitId) p = await api.post<ProductAdmin>(`/admin/products/${p.id}/attach`, { unitId });
        else if (source !== 'order' && count > 0)
          p = await api.post<ProductAdmin>(`/admin/products/${p.id}/units`, {
            count,
            source: source === 'new' ? 'new' : 'buyout',
            state: 'ready',
            place: place || null,
          });
      }
      if (publish) {
        try {
          p = await api.post<ProductAdmin>(`/admin/products/${p.id}/publish`);
        } catch (e) {
          toast(errorText(e), { error: true });
        }
      }
      return p;
    },
    onSuccess: (p) => {
      qc.setQueryData(['product', p.id], p);
      void qc.invalidateQueries({ queryKey: ['stock'] });
      toast(p.status === 'published' ? `${p.sku} опубликована` : `${p.sku} сохранена`);
      if (!id) void navigate(`/stock/${p.id}`, { replace: true });
    },
    onError: (e) => errs.set(e),
  });

  const status = useMutation({
    mutationFn: (action: 'unpublish' | 'archive') => api.post<ProductAdmin>(`/admin/products/${id}/${action}`),
    onSuccess: (p) => {
      qc.setQueryData(['product', p.id], p);
      void qc.invalidateQueries({ queryKey: ['stock'] });
    },
    onError: (e) => toast(errorText(e), { error: true }),
  });

  if (id && existing.isLoading) return <Loader />;
  if (id && existing.isError) return <ErrorBox error={existing.error} onRetry={() => existing.refetch()} />;
  const p = existing.data;

  const addPhotos = async (files: FileList) => {
    setUploading(true);
    try {
      const refs = await uploadFiles(files);
      setF((x) => ({ ...x, photos: [...x.photos, ...refs] }));
    } catch (e) {
      toast(errorText(e), { error: true });
    } finally {
      setUploading(false);
    }
  };
  const addOem = (e: KeyboardEvent<HTMLInputElement>) => {
    if ((e.key === 'Enter' || e.key === ',') && oemDraft.trim()) {
      e.preventDefault();
      if (!f.oems.includes(oemDraft.trim())) set('oems', [...f.oems, oemDraft.trim()]);
      setOemDraft('');
    }
  };

  const hasBench = p ? p.readiness[1].ok : source === 'order' ? !!unitId : f.condition === 'new';
  const checks = [
    { ok: f.photos.length >= 3, label: `Фото: ${f.photos.length} из минимум 3` },
    {
      ok: hasBench,
      label: hasBench ? 'Протокол стенда есть' : f.condition === 'new' ? 'Новая — стенд не обязателен' : 'Нет пройденного стенда',
    },
    {
      ok: f.fitments.some((x) => x.make && x.model),
      label: f.fitments.some((x) => x.make && x.model) ? 'Применимость указана' : 'Укажите применимость',
    },
    { ok: rubToKop(f.price) !== null, label: rubToKop(f.price) !== null ? 'Цена указана' : 'Укажите цену' },
    { ok: f.showOnSite || f.showToPartners, label: 'Выбран канал публикации' },
  ];
  const ready = checks.every((c) => c.ok) && !!f.car.trim();
  const qtyPreview = p ? p.inStock : source === 'order' ? 1 : count;

  return (
    <>
      <PageHead
        crumbs={[{ label: 'Склад и витрина', to: '/stock' }, { label: p ? p.sku : 'Новая позиция' }]}
        title={
          <div className="rk-row" style={{ ['--rk-gap' as string]: '8px 16px', alignItems: 'baseline' }}>
            <h1 className="rk-h1">{p ? 'Позиция витрины' : 'Выставить на продажу'}</h1>
            <span className="rk-mono rk-muted">
              {p
                ? `${p.sku} · ${p.status === 'published' ? 'опубликована' : p.status === 'archived' ? 'в архиве' : 'черновик'}`
                : 'черновик'}
            </span>
          </div>
        }
      />
      <FormMessage message={errs.message} />
      <div className="adm-cols">
        <div className="adm-cols__main">
          {!p && (
            <Panel num="01" title="Источник">
              <div className="rk-stack">
                <Choices
                  label="Источник позиции"
                  value={source}
                  onChange={setSource}
                  min={160}
                  options={[
                    {
                      value: 'order',
                      title: 'Из заказа (обмен)',
                      note: awaiting.length ? `Ждут оформления: ${awaiting.length}` : 'Нет реек после стенда',
                      disabled: !awaiting.length,
                    },
                    { value: 'buyout', title: 'Выкуп / партия', note: 'Уже восстановленные рейки' },
                    { value: 'new', title: 'Новая позиция', note: 'Новые или контрактные' },
                  ]}
                />
                {source === 'order' ? (
                  <Field label="Рейка из заказа" hint="Рейка клиента, восстановленная после обмена и прошедшая стенд">
                    <select className="rk-select rk-input--mono" value={unitId ?? ''} onChange={(e) => setUnitId(e.target.value)}>
                      {awaiting.map((a) => (
                        <option key={a.unitId} value={a.unitId}>
                          {a.number} · {a.car ?? '—'} · {a.rackType ? RACK_TYPE_SHORT[a.rackType] : ''}
                        </option>
                      ))}
                    </select>
                  </Field>
                ) : (
                  <div className="rk-grid-form" style={{ ['--rk-min' as string]: '160px' }}>
                    <Field label="Количество" hint="Столько экземпляров заведём на склад">
                      <input
                        className="rk-input rk-input--mono"
                        type="number"
                        min={1}
                        max={50}
                        value={count}
                        onChange={(e) => setCount(Math.max(1, Number(e.target.value) || 1))}
                      />
                    </Field>
                    <Field label="Место хранения">
                      <input
                        className="rk-input rk-input--mono"
                        value={place}
                        onChange={(e) => setPlace(e.target.value)}
                        placeholder="B-05"
                      />
                    </Field>
                  </div>
                )}
              </div>
            </Panel>
          )}

          <Panel num={p ? '01' : '02'} title="Изделие">
            <div className="rk-stack">
              <div className="rk-grid-form" style={{ ['--rk-min' as string]: '220px' }}>
                <Field label="Автомобиль (заголовок на сайте)" error={errs.errors.car}>
                  <input
                    className="rk-input"
                    value={f.car}
                    onChange={(e) => set('car', e.target.value)}
                    placeholder="Volkswagen Tiguan I"
                  />
                </Field>
                <Field label="Годы">
                  <input
                    className="rk-input rk-input--mono"
                    value={f.years}
                    onChange={(e) => set('years', e.target.value)}
                    placeholder="2007–2016"
                  />
                </Field>
                <Field label="Тип рейки">
                  <Segmented
                    label="Тип рейки"
                    value={f.rackType}
                    onChange={(v) => set('rackType', v)}
                    options={RACK_TYPES.map((t) => ({ value: t, label: RACK_TYPE_SHORT[t] }))}
                  />
                </Field>
                <Field label="Производитель рейки">
                  <input
                    className="rk-input"
                    value={f.manufacturer}
                    onChange={(e) => set('manufacturer', e.target.value)}
                    placeholder="TRW, ZF, Koyo…"
                  />
                </Field>
                <Field label="Руль">
                  <select className="rk-select" value={f.steering} onChange={(e) => set('steering', e.target.value as Steering)}>
                    {STEERING.map((s) => (
                      <option key={s} value={s}>
                        {STEERING_LABEL[s].replace(' руль', '').replace(/^./, (c) => c.toUpperCase())}
                      </option>
                    ))}
                  </select>
                </Field>
                <Field label="Комплектация">
                  <select
                    className="rk-select"
                    value={f.completeness}
                    onChange={(e) => set('completeness', e.target.value as Completeness)}
                  >
                    {COMPLETENESS.map((c) => (
                      <option key={c} value={c}>
                        {COMPLETENESS_LABEL[c].replace(/^./, (x) => x.toUpperCase())}
                      </option>
                    ))}
                  </select>
                </Field>
              </div>
              <Field label="Номера OEM (по ним ищут покупатели)" hint="Введите номер и нажмите Enter">
                <div className="adm-chiplist">
                  {f.oems.map((o) => (
                    <span key={o}>
                      {o}
                      <button
                        type="button"
                        aria-label={`Удалить номер ${o}`}
                        onClick={() =>
                          set(
                            'oems',
                            f.oems.filter((x) => x !== o),
                          )
                        }
                      >
                        ×
                      </button>
                    </span>
                  ))}
                  <input
                    aria-label="Добавить номер OEM"
                    placeholder="+ номер, Enter"
                    value={oemDraft}
                    onChange={(e) => setOemDraft(e.target.value)}
                    onKeyDown={addOem}
                  />
                </div>
              </Field>
              <div className="rk-stack" style={{ ['--rk-gap' as string]: '6px' }}>
                <span className="rk-muted" style={{ fontSize: 12 }}>
                  Применимость
                </span>
                <div className="rk-table-wrap">
                  <div
                    className="rk-table"
                    style={{
                      ['--rk-table-min' as string]: '560px',
                      ['--rk-cols' as string]: 'minmax(0,1fr) minmax(0,1fr) minmax(0,1fr) 110px 44px',
                    }}
                  >
                    <div className="rk-tr rk-tr--head" style={{ padding: '8px 12px' }}>
                      <span>Марка</span>
                      <span>Модель</span>
                      <span>Кузов</span>
                      <span>Годы</span>
                      <span />
                    </div>
                    {f.fitments.map((x, i) => (
                      <div key={i} className="rk-tr" style={{ padding: '6px 12px' }}>
                        {(['make', 'model', 'body', 'years'] as const).map((k) => (
                          <input
                            key={k}
                            className={k === 'years' ? 'rk-input rk-input--mono' : 'rk-input'}
                            style={{ height: 36 }}
                            aria-label={k}
                            value={x[k]}
                            onChange={(e) =>
                              set(
                                'fitments',
                                f.fitments.map((y, j) => (j === i ? { ...y, [k]: e.target.value } : y)),
                              )
                            }
                          />
                        ))}
                        <button
                          type="button"
                          className="rk-btn rk-btn--sm rk-btn--icon"
                          style={{ borderColor: 'transparent' }}
                          aria-label="Удалить строку"
                          onClick={() =>
                            set(
                              'fitments',
                              f.fitments.filter((_, j) => j !== i),
                            )
                          }
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
                <button
                  type="button"
                  className="rk-btn rk-btn--sm rk-btn--link"
                  style={{ alignSelf: 'flex-start' }}
                  onClick={() => {
                    const [make, ...rest] = f.car.split(' ');
                    set('fitments', [
                      ...f.fitments,
                      f.fitments.length
                        ? { make: '', model: '', body: '', years: '' }
                        : { make: make ?? '', model: rest.join(' '), body: '', years: f.years },
                    ]);
                  }}
                >
                  + Добавить автомобиль
                </button>
              </div>
            </div>
          </Panel>

          <Panel num={p ? '02' : '03'} title="Состояние и проверка">
            <div className="rk-stack">
              <div className="rk-grid-form" style={{ ['--rk-min' as string]: '220px' }}>
                <Field label="Состояние">
                  <select className="rk-select" value={f.condition} onChange={(e) => set('condition', e.target.value as Condition)}>
                    {CONDITIONS.map((c) => (
                      <option key={c} value={c}>
                        {CONDITION_LABEL[c]}
                      </option>
                    ))}
                  </select>
                </Field>
                <Field label="Гарантия">
                  <select className="rk-select" value={f.warrantyMonths} onChange={(e) => set('warrantyMonths', Number(e.target.value))}>
                    {[12, 6, 3, 0].map((m) => (
                      <option key={m} value={m}>
                        {m ? `${m} месяцев` : 'Без гарантии'}
                      </option>
                    ))}
                  </select>
                </Field>
              </div>
              <fieldset style={{ border: 0, margin: 0, padding: 0 }}>
                <legend className="rk-muted" style={{ fontSize: 12, marginBottom: 4 }}>
                  Заменено при восстановлении (видно покупателю)
                </legend>
                <div className="adm-checks">
                  {REPLACED_PARTS.map((r) => (
                    <label key={r} className="rk-check" style={{ minHeight: 36 }}>
                      <input
                        type="checkbox"
                        checked={f.replacedParts.includes(r)}
                        onChange={(e) =>
                          set('replacedParts', e.target.checked ? [...f.replacedParts, r] : f.replacedParts.filter((x) => x !== r))
                        }
                      />
                      {r}
                    </label>
                  ))}
                </div>
              </fieldset>
              {p?.bench && (
                <div className="rk-row" style={{ padding: '12px 14px', background: 'var(--panel)', border: '1px solid var(--line)' }}>
                  <span
                    className="rk-mono"
                    style={{
                      width: 40,
                      height: 48,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      background: 'var(--text)',
                      color: 'var(--bg)',
                      fontSize: 10,
                      fontWeight: 700,
                    }}
                  >
                    PDF
                  </span>
                  <span className="rk-grow rk-td--stack">
                    <b>Протокол стенда {p.bench.number}</b>
                    <span className="rk-mono rk-muted" style={{ fontSize: 12 }}>
                      {formatDay(p.bench.testedAt)} · {p.bench.masterName ?? '—'} · {p.bench.passed ? 'все параметры ОК' : 'не пройден'}
                    </span>
                  </span>
                  <span className="rk-badge">
                    <Dot color="var(--success)" size="sm" /> ПРИКРЕПЛЁН
                  </span>
                </div>
              )}
            </div>
          </Panel>

          <Panel
            num={p ? '03' : '04'}
            title="Фото"
            actions={
              <span className="rk-muted" style={{ fontSize: 12 }}>
                Минимум 3. Первое — обложка
              </span>
            }
          >
            <PhotoGrid
              photos={f.photos}
              coverFirst
              onAdd={addPhotos}
              uploading={uploading}
              addLabel="Добавить"
              onRemove={(pid) =>
                set(
                  'photos',
                  f.photos.filter((x) => x.id !== pid),
                )
              }
            />
            {f.photos.length > 1 && (
              <div className="rk-row" style={{ ['--rk-gap' as string]: '6px', marginTop: 10 }}>
                <span className="rk-muted" style={{ fontSize: 12 }}>
                  Сделать обложкой:
                </span>
                {f.photos.slice(1).map((ph, i) => (
                  <button
                    key={ph.id}
                    type="button"
                    className="rk-btn rk-btn--sm"
                    onClick={() => set('photos', [ph, ...f.photos.filter((x) => x.id !== ph.id)])}
                  >
                    фото {i + 2}
                  </button>
                ))}
              </div>
            )}
          </Panel>

          <Panel num={p ? '04' : '05'} title="Цена">
            <div className="rk-grid-form" style={{ ['--rk-min' as string]: '160px' }}>
              <Field label="Цена, ₽" error={errs.errors.priceKop}>
                <input
                  className="rk-input rk-input--mono"
                  inputMode="numeric"
                  value={f.price}
                  onChange={(e) => set('price', e.target.value)}
                  style={{ fontWeight: 700, fontSize: 15 }}
                />
              </Field>
              <Field label="Цена по обмену, ₽" hint="Пусто — обмена нет">
                <input
                  className="rk-input rk-input--mono"
                  inputMode="numeric"
                  value={f.exchangePrice}
                  onChange={(e) => set('exchangePrice', e.target.value)}
                />
              </Field>
              {partnerPrices && (
                <Field label="Партнёрская цена, ₽" hint="Для организаций в кабинете">
                  <input
                    className="rk-input rk-input--mono"
                    inputMode="numeric"
                    value={f.partnerPrice}
                    onChange={(e) => set('partnerPrice', e.target.value)}
                  />
                </Field>
              )}
            </div>
          </Panel>

          <Panel num={p ? '05' : '06'} title="Публикация">
            <div className="rk-stack" style={{ ['--rk-gap' as string]: '0' }}>
              <div className="rk-row" style={{ padding: '10px 0', borderBottom: '1px solid var(--line)', flexWrap: 'nowrap' }}>
                <span className="rk-grow rk-td--stack">
                  <b>Показывать на сайте</b>
                  <span className="rk-muted" style={{ fontSize: 13 }}>
                    Позиция появится в каталоге после публикации
                  </span>
                </span>
                <Switch size="lg" checked={f.showOnSite} onChange={(v) => set('showOnSite', v)} label="Показывать на сайте" />
              </div>
              <div className="rk-row" style={{ padding: '10px 0', borderBottom: '1px solid var(--line)', flexWrap: 'nowrap' }}>
                <span className="rk-grow rk-td--stack">
                  <b>Показывать партнёрам в кабинете</b>
                  <span className="rk-muted" style={{ fontSize: 13 }}>
                    {b2b
                      ? 'Автопарки и СТО видят позицию в обменном фонде и могут зарезервировать'
                      : 'Кабинет для организаций пока выключен — настройка сработает после включения'}
                  </span>
                </span>
                <Switch size="lg" checked={f.showToPartners} onChange={(v) => set('showToPartners', v)} label="Показывать партнёрам" />
              </div>
              <Field label="Комментарий для покупателя" style={{ marginTop: 12 }}>
                <textarea
                  className="rk-textarea"
                  rows={3}
                  value={f.description}
                  onChange={(e) => set('description', e.target.value)}
                  placeholder="Например: следы эксплуатации на корпусе, на работу не влияют"
                />
              </Field>
            </div>
          </Panel>

          {p && <UnitsPanel product={p} />}
        </div>

        <div className="adm-cols__side adm-cols__side--sticky">
          <div className="rk-label">Предпросмотр на сайте</div>
          <article className="rk-card" style={{ borderColor: 'var(--line-2)' }}>
            <CardMedia cover={f.photos[0] ?? null} rackType={f.rackType} />
            <div className="rk-card__body">
              <span className="rk-card__sku">
                {p?.sku ?? 'РК-····'} · {RACK_TYPE_LONG[f.rackType]}
              </span>
              <span className="rk-card__title">{f.car || 'Автомобиль'}</span>
              <span className="rk-card__meta">
                {f.years || 'годы'} · в наличии {qtyPreview} шт
              </span>
              <span className="rk-card__bench">
                <Dot color={hasBench ? 'var(--success)' : 'var(--muted-2)'} />
                {hasBench ? 'СТЕНД: ПРОЙДЕН' : 'СТЕНД: ОЖИДАЕТ'}
              </span>
              <div className="rk-card__foot">
                <div>
                  <div className="rk-price">{rubToKop(f.price) !== null ? formatMoney(rubToKop(f.price)) : '— ₽'}</div>
                  {rubToKop(f.exchangePrice) !== null && (
                    <div className="rk-price__sub">по обмену {formatMoney(rubToKop(f.exchangePrice))}</div>
                  )}
                </div>
              </div>
            </div>
          </article>
          <Panel title="Готовность к публикации" bodyless>
            <ul style={{ margin: 0, padding: '8px 16px', listStyle: 'none' }}>
              {checks.map((c) => (
                <li key={c.label} className="rk-row" style={{ minHeight: 34, ['--rk-gap' as string]: '10px', flexWrap: 'nowrap' }}>
                  <span
                    style={{
                      width: 18,
                      height: 18,
                      flex: 'none',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      background: c.ok ? 'var(--success)' : 'var(--danger)',
                      color: 'var(--bg)',
                      fontSize: 12,
                      fontWeight: 700,
                    }}
                    aria-hidden="true"
                  >
                    {c.ok ? '✓' : '!'}
                  </span>
                  <span style={{ color: c.ok ? 'var(--text-2)' : 'var(--danger-text)' }}>{c.label}</span>
                </li>
              ))}
            </ul>
          </Panel>
          <button
            type="button"
            className="rk-btn rk-btn--primary rk-btn--lg"
            disabled={!ready || save.isPending}
            onClick={() => save.mutate(true)}
          >
            {p?.status === 'published' ? 'Сохранить' : 'Опубликовать'}
          </button>
          {p?.status !== 'published' && (
            <button
              type="button"
              className="rk-btn rk-btn--ghost"
              disabled={save.isPending || !f.car.trim()}
              onClick={() => save.mutate(false)}
            >
              Сохранить черновик
            </button>
          )}
          {p?.status === 'published' && (
            <button type="button" className="rk-btn rk-btn--ghost" disabled={status.isPending} onClick={() => status.mutate('unpublish')}>
              Снять с витрины
            </button>
          )}
          {p && p.status !== 'archived' && p.inStock === 0 && (
            <button type="button" className="rk-btn rk-btn--sm" onClick={() => status.mutate('archive')}>
              В архив
            </button>
          )}
          <Link to="/stock" className="rk-muted" style={{ textAlign: 'center', padding: 8 }}>
            Отмена
          </Link>
        </div>
      </div>
    </>
  );
}

function UnitsPanel({ product: p }: { product: ProductAdmin }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [add, setAdd] = useState(false);
  const [count, setCount] = useState(1);
  const [place, setPlace] = useState('');
  const refresh = (np: ProductAdmin) => {
    qc.setQueryData(['product', np.id], np);
    void qc.invalidateQueries({ queryKey: ['stock'] });
  };
  const addUnits = useMutation({
    mutationFn: () =>
      api.post<ProductAdmin>(`/admin/products/${p.id}/units`, { count, source: 'buyout', state: 'ready', place: place || null }),
    onSuccess: (np) => {
      refresh(np);
      setAdd(false);
      toast(`Добавлено: ${count} шт.`);
    },
    onError: (e) => toast(errorText(e), { error: true }),
  });
  const patchUnit = useMutation({
    mutationFn: ({ id, body }: { id: string; body: Record<string, unknown> }) => api.patch<Unit>(`/admin/units/${id}`, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['product', p.id] }),
    onError: (e) => toast(errorText(e), { error: true }),
  });
  const release = useMutation({
    mutationFn: (rid: string) => api.del(`/admin/reservations/${rid}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['product', p.id] });
      toast('Резерв снят');
    },
  });
  return (
    <Panel
      title={`Экземпляры · ${p.units.length}`}
      actions={
        <button type="button" className="rk-btn rk-btn--sm" onClick={() => setAdd(true)}>
          + Добавить
        </button>
      }
      bodyless
    >
      <div className="rk-rows">
        {p.units.map((u) => (
          <div key={u.id} className="rk-row" style={{ padding: '10px 18px', justifyContent: 'space-between' }}>
            <span className="rk-td--stack" style={{ minWidth: 0 }}>
              <span className="rk-row" style={{ ['--rk-gap' as string]: '8px' }}>
                <Link to={`/units/${u.id}`} className="rk-mono" style={{ fontWeight: 700 }}>
                  {u.code}
                </Link>
                <span className="rk-badge">
                  <Dot color={u.state === 'ready' ? (u.reservation ? 'var(--warn)' : 'var(--success)') : 'var(--muted-2)'} size="sm" />
                  {u.ownerKind !== 'company'
                    ? `У КЛИЕНТА${u.ownerName ? ` · ${u.ownerName}` : ''}`
                    : u.reservation
                      ? 'РЕЗЕРВ'
                      : UNIT_STATE_LABEL[u.state].toUpperCase()}
                </span>
              </span>
              <span className="rk-muted" style={{ fontSize: 12 }}>
                {UNIT_SOURCE_LABEL[u.source]}
                {u.sourceOrder ? ` · ${u.sourceOrder.number}` : ''}
                {u.bench ? ` · стенд ${u.bench.number} ${u.bench.passed ? 'ОК' : 'не пройден'}` : ''}
                {u.reservation
                  ? ` · резерв: ${u.reservation.forName ?? u.reservation.orderNumber ?? '—'}${u.reservation.until ? ` до ${formatDay(u.reservation.until)}` : ''}`
                  : ''}
              </span>
            </span>
            <span className="rk-row" style={{ ['--rk-gap' as string]: '8px' }}>
              {u.ownerKind === 'company' && (
                <input
                  className="rk-input rk-input--mono"
                  style={{ width: 80, height: 36 }}
                  aria-label={`Место хранения ${u.code}`}
                  defaultValue={u.place ?? ''}
                  placeholder="место"
                  onBlur={(e) =>
                    e.target.value !== (u.place ?? '') && patchUnit.mutate({ id: u.id, body: { place: e.target.value || null } })
                  }
                />
              )}
              {u.reservation && (
                <button type="button" className="rk-btn rk-btn--sm" onClick={() => release.mutate(u.reservation!.id)}>
                  Снять резерв
                </button>
              )}
              <a className="rk-btn rk-btn--sm" href={`/api/print/units/${u.id}/label`} target="_blank" rel="noreferrer">
                Бирка
              </a>
            </span>
          </div>
        ))}
        {!p.units.length && (
          <div className="rk-panel__body">
            <Hint>Экземпляров нет — добавьте выкупленные рейки или привяжите рейку после стенда.</Hint>
          </div>
        )}
      </div>
      <Modal
        open={add}
        onClose={() => setAdd(false)}
        title="Добавить экземпляры"
        footer={
          <button type="button" className="rk-btn rk-btn--primary" disabled={addUnits.isPending} onClick={() => addUnits.mutate()}>
            Добавить {count} шт.
          </button>
        }
      >
        <Hint>Готовые восстановленные рейки этой позиции — выкуп или партия. Каждая получит свою бирку.</Hint>
        <div className="rk-grid-form">
          <Field label="Количество">
            <input
              className="rk-input rk-input--mono"
              type="number"
              min={1}
              max={50}
              value={count}
              onChange={(e) => setCount(Math.max(1, Number(e.target.value) || 1))}
            />
          </Field>
          <Field label="Место хранения">
            <input className="rk-input rk-input--mono" value={place} onChange={(e) => setPlace(e.target.value)} />
          </Field>
        </div>
      </Modal>
    </Panel>
  );
}
